import { ContactPoint, FhirResource, HealthcareService, Organization, Patient, Practitioner, PractitionerRole, RelatedPerson } from 'src/careflow/ph-core/fhir-client/resources';
import { HealthcareServiceWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/HealthcareService';
import { OrganizationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { PatientWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { PractitionerWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import { PractitionerRoleWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import { RelatedPersonWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/RelatedPerson';
import { orderTelecomByRank } from 'src/utils/fhir';
import type { IcomxContactCandidate } from './icomxContactCandidateModel';

export type { IcomxContactCandidate, IcomxTelecomSystem } from './icomxContactCandidateModel';
export {
  dedupeIcomxContactCandidates,
  filterIcomxCandidatesByTelecomSystem,
  filterLinkedIcomxCandidatesByTelecomSystem,
  getIcomxCandidateResourceDescriptor,
  hasAnyIcomxCandidateTelecom,
  hasIcomxCandidateTelecom,
  hasIcomxLinkedResource,
  isIcomxLinkedResourceType,
} from './icomxContactCandidateModel';

interface PractitionerRoleCandidateOptions {
  practitioner?: Practitioner | null;
  organization?: Organization | null;
}

function getOrderedTelecom(telecom: ContactPoint[] | null | undefined) {
  return orderTelecomByRank([...(telecom ?? [])]);
}

function getPhoneAndEmail(telecom: ContactPoint[] | null | undefined) {
  const orderedTelecom = getOrderedTelecom(telecom);
  const phone = orderedTelecom.find(({ system, value }) => system === 'phone' && !!value)?.value;
  const email = orderedTelecom.find(({ system, value }) => system === 'email' && !!value)?.value;

  return {
    phone,
    email,
    telecomSummary: [phone, email].filter((value): value is string => !!value),
  };
}

function normalizeNameParts(fullName: string | null | undefined, fallbackLastName: string) {
  const normalized = fullName?.trim() ?? '';

  if (!normalized) {
    return {
      firstName: fallbackLastName,
      lastName: fallbackLastName,
    };
  }

  const parts = normalized.split(/\s+/);

  if (parts.length === 1) {
    return {
      firstName: parts[0],
      lastName: fallbackLastName || parts[0],
    };
  }

  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
}

export function buildPractitionerCandidate(practitioner: Practitioner, sourceLabel?: string): IcomxContactCandidate {
  const wrapped = PractitionerWrapper(practitioner);
  const fullName = wrapped.getFullName() ?? 'Practitioner';
  const { firstName, lastName } = normalizeNameParts(fullName, 'Practitioner');
  const { phone, email, telecomSummary } = getPhoneAndEmail(practitioner.telecom);

  return {
    id: `Practitioner/${practitioner.id ?? fullName}`,
    label: fullName,
    subtitle: wrapped.getNPI?.() || undefined,
    resourceType: 'Practitioner',
    resourceId: practitioner.id,
    firstName,
    lastName,
    phone,
    email,
    telecomSummary,
    integrationId: practitioner.id,
    sourceLabel,
  };
}

export function buildRelatedPersonCandidate(relatedPerson: RelatedPerson, sourceLabel?: string): IcomxContactCandidate {
  const wrapped = RelatedPersonWrapper(relatedPerson);
  const fullName = wrapped.getFullName() ?? 'Related Person';
  const { firstName, lastName } = normalizeNameParts(fullName, 'Related Person');
  const { phone, email, telecomSummary } = getPhoneAndEmail(relatedPerson.telecom);

  return {
    id: `RelatedPerson/${relatedPerson.id ?? fullName}`,
    label: fullName,
    resourceType: 'RelatedPerson',
    resourceId: relatedPerson.id,
    firstName,
    lastName,
    phone,
    email,
    telecomSummary,
    integrationId: relatedPerson.id,
    sourceLabel,
  };
}

export function buildPatientCandidate(patient: Patient, sourceLabel?: string): IcomxContactCandidate {
  const wrapped = PatientWrapper(patient);
  const fullName = wrapped.getFullName?.() ?? 'Patient';
  const { firstName, lastName } = normalizeNameParts(fullName, 'Patient');
  const { phone, email, telecomSummary } = getPhoneAndEmail(patient.telecom);

  return {
    id: `Patient/${patient.id ?? fullName}`,
    label: fullName,
    subtitle: wrapped.getMRN?.('')?.value,
    resourceType: 'Patient',
    resourceId: patient.id,
    firstName,
    lastName,
    phone,
    email,
    telecomSummary,
    integrationId: patient.id,
    sourceLabel,
  };
}

export function buildOrganizationCandidate(organization: Organization, sourceLabel?: string): IcomxContactCandidate {
  const wrapped = OrganizationWrapper(organization);
  const label = organization.name ?? 'Organization';
  const { phone, email, telecomSummary } = getPhoneAndEmail(organization.telecom);

  return {
    id: `Organization/${organization.id ?? label}`,
    label,
    subtitle: wrapped.getType?.() || undefined,
    resourceType: 'Organization',
    resourceId: organization.id,
    firstName: label,
    lastName: 'Organization',
    phone,
    email,
    telecomSummary,
    integrationId: organization.id,
    sourceLabel,
  };
}

export function buildHealthcareServiceCandidate(healthcareService: HealthcareService, sourceLabel?: string): IcomxContactCandidate {
  const wrapped = HealthcareServiceWrapper(healthcareService);
  const label = healthcareService.name ?? 'Healthcare Service';
  const { phone, email, telecomSummary } = getPhoneAndEmail(healthcareService.telecom);

  return {
    id: `HealthcareService/${healthcareService.id ?? label}`,
    label,
    subtitle: wrapped.getMINT?.('')?.value,
    resourceType: 'HealthcareService',
    resourceId: healthcareService.id,
    firstName: label,
    lastName: 'Healthcare Service',
    phone,
    email,
    telecomSummary,
    integrationId: healthcareService.id,
    sourceLabel,
  };
}

export function buildPractitionerRoleCandidate(
  practitionerRole: PractitionerRole,
  related?: PractitionerRoleCandidateOptions,
  sourceLabel?: string
): IcomxContactCandidate {
  const wrapped = PractitionerRoleWrapper(practitionerRole);
  const practitionerName = related?.practitioner ? PractitionerWrapper(related.practitioner).getFullName() : null;
  const label = practitionerName || practitionerRole.practitioner?.display || wrapped.getRoleName() || 'Practitioner Role';
  const { firstName, lastName } = normalizeNameParts(label, 'Practitioner');
  const { phone, email, telecomSummary } = getPhoneAndEmail(practitionerRole.telecom);

  return {
    id: `PractitionerRole/${practitionerRole.id ?? label}`,
    label,
    subtitle: related?.organization?.name || practitionerRole.organization?.display || wrapped.getRoleName() || undefined,
    resourceType: 'PractitionerRole',
    resourceId: practitionerRole.id,
    firstName,
    lastName,
    phone,
    email,
    telecomSummary,
    integrationId: practitionerRole.id,
    sourceLabel,
  };
}

export function buildIcomxCandidateFromResource(
  resource: FhirResource | null | undefined,
  options?: {
    sourceLabel?: string;
    practitionerRole?: PractitionerRoleCandidateOptions;
  }
): IcomxContactCandidate | null {
  if (!resource) {
    return null;
  }

  switch (resource.resourceType) {
    case 'Practitioner':
      return buildPractitionerCandidate(resource as Practitioner, options?.sourceLabel);
    case 'RelatedPerson':
      return buildRelatedPersonCandidate(resource as RelatedPerson, options?.sourceLabel);
    case 'Patient':
      return buildPatientCandidate(resource as Patient, options?.sourceLabel);
    case 'Organization':
      return buildOrganizationCandidate(resource as Organization, options?.sourceLabel);
    case 'HealthcareService':
      return buildHealthcareServiceCandidate(resource as HealthcareService, options?.sourceLabel);
    case 'PractitionerRole':
      return buildPractitionerRoleCandidate(
        resource as PractitionerRole,
        options?.practitionerRole,
        options?.sourceLabel
      );
    default:
      return null;
  }
}
